import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { useOnboardingStore } from '../store/useOnboardingStore';
import { usePostsPoll, useProfilePoll } from '../queries/onboardingPolls';
import { useCreateProfile } from '../mutations/onboarding';
import useSummaryAndOfferings from './useSummaryAndOfferings';
import { validateURL } from '../utils/helpers';
import useErrorModalStore from '../store/useErrorModalStore';
import { getErrorModalMessage } from '../utils/errorMessages';

export const useOnboarding = () => {
  const [websiteUrlInput, setWebsiteUrlInput] = useState('');
  const { openErrorModal, isOpen } = useErrorModalStore();
  const navigate = useNavigate();
  const { accessKey } = useAppStore();

  const {
    websiteUrl,
    flowState,
    currentStep,
    profileError,
    analysisProgress,
    setCurrentStep,
    setProfileError,
    setAnalysisProgress,
    startAnalysis,
    markProfileReady,
    startPostGeneration,
    resetOnboarding,
    retryProfileCreation,
    completeOnboarding,
  } = useOnboardingStore();

  const handleWebsiteSubmit = (e) => {
    e.preventDefault();

    const { isValid, url } = validateURL(websiteUrlInput);
    if (!isValid) {
      setProfileError(
        'Please enter a valid website URL (e.g., https://example.com or example.com)'
      );
      return;
    }

    startAnalysis(url);
    createProfileMutation.mutate({ accessKey, websiteUrl: url });
  };

  const handleRetry = () => {
    const url = retryProfileCreation();
    if (url) {
      createProfileMutation.mutate({ accessKey, websiteUrl: url });
    }
  };

  const handleStepChange = (stepIndex) => {
    setCurrentStep(stepIndex);

    if (stepIndex === 2 && flowState === 'profile_ready') {
      startPostGeneration();
    }
  };

  const handleFinish = () => {
    if (firstPendingStoryPost?.status === 'pending' || firstPendingFeedPost?.status === 'pending') {
      completeOnboarding();
      navigate('/dashboard', { replace: true });
    }
  };

  const createProfileMutation = useCreateProfile(
    () => {},
    (error) => {
      setProfileError(error.message || 'Failed to create profile');
    }
  );

  const profilePoll = useProfilePoll(
    accessKey,
    flowState === 'analyzing' && !createProfileMutation.isPending && !isOpen
  );

  const shouldFetchSummaryOfferings =
    flowState === 'profile_ready' || flowState === 'generating_post' || flowState === 'completed';
  const {
    profileSummary,
    summaryLoading,
    summaryError,
    profileOfferings,
    offeringsLoading,
    offeringsError,
    handleSaveSummary,
    handleSaveOffering,
    handleDeleteOffering,
    handleAddOffering,
    updateSummaryMutation,
    updateOfferingMutation,
  } = useSummaryAndOfferings(shouldFetchSummaryOfferings);

  const handlePostsError = (error) => {
    // Handle timeout - navigate to dashboard
    if (error?.status === 404) {
      return;
    }

    // Handle other errors - show modal with action to go to dashboard
    openErrorModal(getErrorModalMessage('onboarding_posts_error'), 'onboarding_posts_error', {
      title: 'Go to Dashboard',
      action: () => {
        completeOnboarding();
        navigate('/dashboard', { replace: true });
      },
    });
  };

  const shouldPollPosts = flowState === 'generating_post';
  const { data: postsData, isError: isErrorPosts } = usePostsPoll(
    shouldPollPosts,
    'onboarding',
    null,
    handlePostsError
  );

  const firstPendingStoryPost = useMemo(() => {
    if (!Array.isArray(postsData)) return [];
    return postsData?.find((post) => post.status === 'pending' && post.aspectRatio === '9:16');
  }, [postsData]);

  const firstPendingFeedPost = useMemo(() => {
    if (!Array.isArray(postsData)) return [];
    return postsData?.find((post) => post.status === 'pending' && post.aspectRatio === '1:1');
  }, [postsData]);

  useEffect(() => {
    if (websiteUrl) setWebsiteUrlInput(websiteUrl);
  }, [websiteUrl]);

  useEffect(() => {
    if (!profilePoll?.isFetching && profilePoll.data?.status) {
      const status = profilePoll.data.status;
      if (status === 'failed') {
        setProfileError('Failed to create profile. Please try again.');
        openErrorModal(
          getErrorModalMessage('profile_check_error'),
          'profile_check_error',
          {
            title: 'Start Over',
            action: () => resetOnboarding(),
          },
          { websiteUrl: websiteUrlInput }
        );
      } else if (status === 'new') {
        setAnalysisProgress(70);
      } else if (status === 'created') {
        setAnalysisProgress(100);
        markProfileReady();
      }
    }

    if (profilePoll.isError) {
      setProfileError('An error occurred while analyzing the website. Please try again.');
      openErrorModal(
        getErrorModalMessage('profile_check_error'),
        'profile_check_error',
        {
          title: 'Start Over',
          action: () => resetOnboarding(),
        },
        { websiteUrl }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    profilePoll?.data,
    profilePoll?.isError,
    profilePoll?.isFetching,
    setAnalysisProgress,
    markProfileReady,
    setProfileError,
    openErrorModal,
    resetOnboarding,
  ]);

  useEffect(() => {
    if (flowState === 'profile_ready') {
      setCurrentStep(1);
    }
  }, [flowState, currentStep, setCurrentStep]);

  useEffect(() => {
    const hasAnyPending =
      firstPendingStoryPost?.status === 'pending' || firstPendingFeedPost?.status === 'pending';
    if (hasAnyPending && flowState === 'generating_post') {
      setCurrentStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstPendingStoryPost, firstPendingFeedPost, flowState]);

  return {
    websiteUrlInput,
    setWebsiteUrlInput,
    websiteUrl,
    flowState,
    currentStep,
    profileError,
    analysisProgress,
    handleWebsiteSubmit,
    handleRetry,
    handleStepChange,
    handleFinish,
    createProfileMutation,
    profileSummary,
    summaryLoading,
    summaryError,
    profileOfferings,
    offeringsLoading,
    offeringsError,
    handleSaveSummary,
    handleSaveOffering,
    handleDeleteOffering,
    updateSummaryMutation,
    updateOfferingMutation,
    firstPendingStoryPost,
    firstPendingFeedPost,
    handleAddOffering,
    isErrorPosts,
  };
};
